import PropTypes from "prop-types";
import React from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierExpandableHeader from "@/Layouts/components/Tiers/TierExpandableHeader";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Custom components
import ComposedRow from "./ComposedRow";

// Materials
import { Grid, Stack } from "@mui/material";

const newLocalTier = (metric = "PVR", inputCash = "Percent", inputLease = "Percent") => {
	return {
		from: {
			input: metric === "PVR" ? "Dollar" : "Unit",
			value: 0,
		},
		to: {
			input: metric === "PVR" ? "Dollar" : "Unit",
			value: 1000000,
		},
		cash: {
			input: inputCash,
			value: 0,
		},
		lease: {
			input: inputLease,
			value: 0,
		},
	};
};

const metrics = {
	PVR: {
		input: "Dollar",
		decimalsEditable: true,
		decimals: 2,
	},
	PP: {
		input: "Unit",
		decimalsEditable: true,
		decimals: 2,
	},
};

const getMetricLabel = (metric = "PVR") => {
	const labels = {
		PVR: "Profit per Vehicle Retailed (PVR)",
		PP: "Product Penetration (PP)",
	};
	return labels[metric];
};

export default function ComposedTiers(props) {
	const {
		model = {
			metric: "PVR",
			bonus: false,
			tiers: [],
		},
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	// Specific use of concatenatedErrors to get all errors in one list
	const { concatenatedErrors, isValid, getErrorsMessages, validate } = useValidations();
	const errorsMessages = getErrorsMessages();

	// Control to send update actions / References
	const wasUpdated = React.useRef(false);

	const onModelUpdate = (updatedModel) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedModel);
		}
	};

	// States
	const [innerModel, setInnerModel] = useCallbackState([], onModelUpdate);

	const handleAddTier = () => {
		const tiers = [...innerModel.tiers];
		const lastIndex = tiers?.length ? tiers.length - 1 : 0;
		const inputCashBump = tiers?.[lastIndex]?.["cash"]?.["input"] || "Percent";
		const inputLeaseBump = tiers?.[lastIndex]?.["lease"]?.["input"] || "Percent";

		if (tiers?.length) {
			tiers[lastIndex].to.value = 0;
		}

		tiers.push(newLocalTier(innerModel.metric, inputCashBump, inputLeaseBump));
		wasUpdated.current = false;
		setInnerModel({ ...innerModel, tiers });
	};

	const handleRemoveTier = () => {
		const tiers = [...innerModel.tiers];
		if (tiers.length > 1) {
			tiers.splice(-1);
		}
		const lastIndex = tiers?.length ? tiers.length - 1 : 0;
		tiers[lastIndex].to.value = 1000000;
		updateModel({ ...innerModel, tiers });
	};

	const handleUpdateValue = (index, area, value, delta = 0) => {
		const tiers = [...innerModel.tiers];
		tiers[index][area]["value"] = value ? Number(value) : 0;
		if (tiers?.[index + 1] && area === "to") {
			tiers[index + 1]["from"]["value"] = Number(value) + delta;
		}
		updateModel({ ...innerModel, tiers });
	};

	const handleChangeType = (_, area, input) => {
		const tiers = [...innerModel.tiers];
		tiers.forEach((tier) => {
			tier[area]["input"] = input;
			tier[area]["value"] = 0;
		});
		updateModel({ ...innerModel, tiers });
	};

	const handleChangeMeasure = (value) => {
		const tiers = [...innerModel.tiers];
		const input = metrics?.[value]?.input || "Percent";

		tiers.forEach((tier) => {
			tier["from"]["input"] = input;
			tier["to"]["input"] = input;
			tier["from"]["value"] = 0;
			tier["to"]["value"] = 0;
		});

		tiers[tiers.length - 1]["to"]["value"] = 1000000;
		updateModel({ ...innerModel, metric: value, tiers });
	};

	const updateModel = (updatedModel) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	React.useMemo(() => {
		if (innerModel?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "compensation",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0.01, Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerModel.tiers || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
								{
									order: "ROWS",
									type: "ANY",
									on: "value",
									distinct: 0,
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: (results = new Map(), section = []) => {
									if (!results.size) {
										return [];
									}

									const columns = results?.get("COLUMNS") || [];
									const rows = results?.get("ROWS") || [];

									const cashEmpty = section?.every((element) => !element?.cash?.value);
									const leaseEmpty = section?.every((element) => !element?.lease?.value);

									if (cashEmpty && leaseEmpty) {
										return rows.map(() => ({ cash: true, lease: true }));
									}

									const isSomeValueInLine = (sectionRef, index = 0) => {
										const { cash, lease } = sectionRef?.[index] || {};
										return cash?.value || lease?.value;
									};

									if (rows?.length === 1) {
										const isEmptyError = !isSomeValueInLine(section, 0);
										if (isEmptyError) {
											return [{ cash: true, lease: true }];
										}
									}

									const mapTotalInvalid = new Map();

									for (const index of ["cash", "lease"]) {
										mapTotalInvalid.set(
											index,
											columns.every((element) => element[index])
										);
									}

									for (let i = 0; i < rows.length; i++) {
										for (const index of ["cash", "lease"]) {
											const checkOn = rows[i][index] !== columns[i][index];
											if (checkOn) {
												const value = section?.[i]?.[index]?.["value"];
												const previousValue = section?.[i - 1]?.[index]?.["value"];

												const maxRule =
													isNumeric(value) && isNumeric(previousValue)
														? Number(value) < Number(previousValue)
														: false;
												const option = mapTotalInvalid.get(index);
												if (!option) {
													rows[i][index] = columns?.[i]?.[index] || false;
												}
												if (maxRule) {
													rows[i][index] = true;
													if (rows?.[i - 1]) {
														rows[i - 1][index] = true;
													}
												}
											}
										}
									}

									return [...rows];
								},
							},
						},
						errorsMap: ["cash", "lease"],
						message: "Missing commission",
						validationType: "COMPOSED",
					},
					section: innerModel.tiers || [],
				},
			]);
		}
	}, [innerModel, validate]);

	React.useEffect(() => {
		setInnerModel(clone(model));

		return () => {
			setInnerModel(null);
			wasUpdated.current = false;
		};
	}, [model, setInnerModel]);

	return (
		<TierRows
			deleteButtonText={"Remove Tier"}
			addButtonText={"Add Tier"}
			deleteButtonDisabled={Boolean(innerModel?.tiers?.length <= 1) || readOnly}
			addButtonDisabled={readOnly}
			spaces={{
				mainSection: 255,
				columns: 99,
				totalColumns: 2,
				totalRows: innerModel?.tiers?.length || 1,
				separation: 10,
			}}
			containerProps={{
				position: "relative",
				mt: 0.5,
			}}
			onClickDelete={handleRemoveTier}
			onClickAdd={handleAddTier}
		>
			<TierExpandableHeader
				headers={[
					{
						component: (
							<Stack ml={"-14px"} minWidth={"210px"}>
								<SmartSelector
									options={[
										{ _id: "PVR", label: getMetricLabel("PVR") },
										{ _id: "PP", label: getMetricLabel("PP") },
									]}
									selected={{ _id: innerModel?.metric || "PVR", label: getMetricLabel(innerModel?.metric || "PVR") }}
									inputProps={{
										placeholder: "Select metric...",
									}}
									listBoxProps={{
										sx: { maxHeight: "195px" },
									}}
									disabled={readOnly}
									onChangeAction={(selected) => handleChangeMeasure(selected._id)}
								/>
							</Stack>
						),
						style: { minWidth: "255px" },
					},
					{
						title: "Cash Bump",
						titleClassName: "tiered-title !ml-[10px]",
						showDivider: true,
						itemProps: { sm: 12, md: 1.5 },
					},
					{
						title: "Lease Bump",
						titleClassName: "tiered-title !ml-[2px]",
						showDivider: true,
						itemProps: { sm: 12, md: 1.5, ml: "-5px" },
					},
					{
						component: (
							<Stack direction={"row"}>
								<CustomizedSwitches
									textLeft
									textChecked="Bonus"
									textUnChecked="Default"
									checked={Boolean(innerModel?.bonus)}
									onChange={(value) => {
										updateModel({ ...innerModel, bonus: value });
									}}
									disabled={readOnly}
									tooltip={{
										title: "Apply as bonus or default commission.",
										placement: "bottom",
										key: "cash-and-lease-bump-bonus-tooltip",
									}}
								/>
							</Stack>
						),
						style: { position: "absolute", right: 0 },
					},
				]}
				containerProps={{
					direction: "row",
					justifyContent: "flex-start",
					columnSpacing: 4,
				}}
			/>

			<Grid container direction={"row"} spacing={2} mt={1}>
				{innerModel?.tiers?.map((element, key) => {
					return (
						<Grid key={`composed_row_${innerModel?.metric}_${key}`} container item>
							<ComposedRow
								index={key}
								item={element}
								spaces={{
									mainSection: 255,
									columns: 99,
									totalColumns: 2,
									separation: 10,
									reduction: 0,
								}}
								hasShadow={!(key % 2)}
								metrics={metrics[innerModel?.metric]}
								fromDisabled={Boolean(!key) || readOnly}
								toDisabled={Boolean(innerModel?.tiers?.length === key + 1) || readOnly}
								errors={concatenatedErrors?.[key] || null}
								readOnly={readOnly}
								onActionUpdate={handleUpdateValue}
								onChangeType={handleChangeType}
							/>
						</Grid>
					);
				})}
			</Grid>

			{Boolean(errorsMessages?.length) && (
				<Grid container direction="column" rowSpacing={0.2} mt={errorsMessages?.length ? 1 : 0.5} alignItems="flex-start">
					{errorsMessages.map((message, key) => (
						<TierAlertError key={`error_composed_tiers_${message}_${key}`} text={message} />
					))}
				</Grid>
			)}
		</TierRows>
	);
}
ComposedTiers.propTypes = {
	model: PropTypes.shape({
		metric: PropTypes.string,
		bonus: PropTypes.bool,
		tiers: PropTypes.array,
	}),
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
